import React, {
  ReactNode, useContext, useEffect, useState,
} from 'react'
import { JsonObject } from 'type-fest'
import { generalStore } from '../../sessionStorage'
import { Product } from '../../utils/commonInterfaces'
import { Redirecter } from '../../utils/useRedirecter'
import { PackageProps } from '../Packages/Package'
import { BundlesProps } from '../Bundles/Bundles'
import { getButterPage } from '../../butter'

export interface GeneralOptions extends JsonObject {
  showStickyCheckAvailability: boolean;
  warningMessageType?: string;
  closedWarningMessageType?: string;
  eligibleForHiddenOffers: boolean;
  visibleProducts: Product[];
  closedBannerWithIcon: boolean;
  activeSpeedTabMobile?: number;
  urlPath?: string;
  sprn?: number;
  alternativePhone?: boolean;
  installationBoroughs?: Array<string>;
  rescheduleBoroughs?: Array<string>;
  trustpilotWidgets?: Array<string>;
  providers?: Array<string>;
  trustpilotId?: string;
  borough?: string | null;
  customerCategory: string;
  customerSegment: string;
  defaultRafId?: string;
  checkoutData?: any;
  generalSettingPage?: GeneralSettingPageProps;
  premiseLevelPricingOffer?: string;
  premiseLevelPricingStartDate?: string;
  premiseLevelPricingEndDate?: string;
}
interface GeneralSettingPageProps extends JsonObject {
  [key: string]: any;
  redirecter?: Redirecter;
  skip_appointments?: SkipAppointmentsProps;
  box_redirect?: string;
  hiden_for_box?: any;
  general?: any;
  billing_cycle?: any;
  monthly_cost?: boolean;
  exclude_affiliates_from_promo?: boolean;
}
interface SkipAppointmentsProps {
  skip_appointment_package_list?: PackageProps[];
  skip_appointment_bundle_list?: BundlesProps[];
}

async function getGeneralSettings(): Promise<GeneralSettingPageProps> {
  const generalSettings: any = await getButterPage('general-settings', {})
  const componentTypes = [
    'redirecter',
    'skip_appointments',
    'box_redirect',
    'hiden_for_box',
    'general',
    'billing_cycle',
    'monthly_cost',
    'exclude_affiliates_from_promo',
  ] as const

  return componentTypes.reduce((settingsObject, type) => {
    const component = generalSettings.components.find((c: any) => c.type === type)
    if (component) {
      const {
        type, ...componentData
      } = component

      settingsObject[type] = Object.keys(componentData).length === 1 ?
        Object.values(componentData)[0] :
        componentData
    }

    return settingsObject
  }, {} as GeneralSettingPageProps)
}

export const INITIAL_GENERAL_OPTIONS: GeneralOptions = {
  showStickyCheckAvailability: true,
  eligibleForHiddenOffers: false,
  visibleProducts: [],
  closedBannerWithIcon: false,
  activeSpeedTabMobile: 1,
  urlPath: undefined,
  sprn: undefined,
  alternativePhone: undefined,
  installationBoroughs: undefined,
  rescheduleBoroughs: undefined,
  providers: undefined,
  trustpilotWidgets: undefined,
  trustpilotId: undefined,
  borough: undefined,
  customerCategory: 'RES',
  customerSegment: 'RESIDENTIAL',
  defaultRafId: '',
  generalSettingPage: undefined,
  premiseLevelPricingOffer: undefined,
  premiseLevelPricingStartDate: undefined,
  premiseLevelPricingEndDate: undefined,
}

interface GeneralContextType {
  generalOptions: GeneralOptions;
  setGeneralOptions: React.Dispatch<React.SetStateAction<GeneralOptions>>;
}

const GeneralContext = React.createContext<GeneralContextType | null>(null)

export function useGeneralContext() {
  const context = useContext(GeneralContext)

  if (!context) {
    throw new Error('Ensure you use inside GeneralContext Provider')
  }

  return context
}

export function GeneralContextProvider({ children }: { children: ReactNode }) {
  const [
    generalOptions,
    setGeneralOptions,
  ] = useState<GeneralOptions>(INITIAL_GENERAL_OPTIONS)

  useEffect(() => {
    async function fetchSavedGeneralContext() {
      try {
        const [
          savedGeneralContext,
          generalSettings,
        ] = await Promise.all([
          generalStore.get(),
          getGeneralSettings(),
        ])

        setGeneralOptions((prevState) => ({
          ...prevState,
          ...(savedGeneralContext || {}),
          generalSettingPage: generalSettings,
        }))
      } catch (err) {
        console.error('Error fetching general context:', err)
      }
    }

    fetchSavedGeneralContext()
  }, [])

  useEffect(() => {
    async function persistGeneralState() {
      if (JSON.stringify(generalOptions) === JSON.stringify(INITIAL_GENERAL_OPTIONS)) {
        return
      }

      await generalStore.set(generalOptions)
    }

    persistGeneralState()
  }, [generalOptions])

  return (
    <GeneralContext.Provider value={{
      generalOptions,
      setGeneralOptions,
    }}
    >
      {children}
    </GeneralContext.Provider>
  )
}
